// src/components/cards/CardGrid.tsx
import React, { useState, useEffect } from "react";
import { searchCards, PokemonCard } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

interface CardGridProps {
  pageSize?: number;
  initialQuery?: string;
  similarTo?: PokemonCard;  // New prop for similar cards functionality
}

export const CardGrid: React.FC<CardGridProps> = ({
  pageSize = 10,
  initialQuery = "",
  similarTo,
}) => {
  const [cards, setCards] = useState<PokemonCard[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset page when query changes
  useEffect(() => {
    let searchQuery = initialQuery;
    
    // If we're looking for similar cards, build a query
    if (similarTo) {
      // Simplified query approach to avoid API errors
      // Just search by the Pokemon name since it's the most reliable way to find similar cards
      if (similarTo.name) {
        // Extract just the base Pokemon name (first word)
        const baseName = similarTo.name.split(' ')[0].trim();
        // Exclude the current card from results
        searchQuery = `name:"${baseName}" -id:${similarTo.id}`;
      }
      
      // If no name is available, fallback to set matching
      else if (similarTo.set?.id) {
        searchQuery = `set.id:${similarTo.set.id} -id:${similarTo.id}`;
      }
    }
    
    console.log("Setting search query:", searchQuery);
    setQuery(searchQuery);
    setPage(1);
  }, [initialQuery, similarTo]);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { data, totalCount } = await searchCards({
          q: query,
          page,
          pageSize,
        });
        if (!canceled) {
          setCards(data);
          setTotalCount(totalCount);
        }
      } catch (err: any) {
        if (err.name !== "AbortError") {
          console.error(err);
          setError("Failed to load cards.");
        }
      } finally {
        if (!canceled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      canceled = true;
    };
  }, [query, page, pageSize]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {Array(pageSize).fill(0).map((_, i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-0 h-64 animate-pulse bg-gray-200" />
          </Card>
        ))}
      </div>
    );
  }
  
  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }
  
  if (cards.length === 0) {
    return <div className="text-center py-8">No cards found.</div>;
  }

  const pageCount = Math.ceil(totalCount / pageSize);

  // Get market trend for a card
  const getMarketTrend = (card: PokemonCard) => {
    if (!card.tcgplayer?.prices) return null;
    const priceData = Object.values(card.tcgplayer.prices)[0];
    
    if (!priceData || !priceData.market) return null;
    
    const marketPrice = priceData.market;
    const avgPrice = (priceData.low + priceData.high) / 2;
    
    if (marketPrice > avgPrice * 1.1) return "rising";
    if (marketPrice < avgPrice * 0.9) return "falling";
    return "stable";
  };

  // Get price for a card
  const getCardPrice = (card: PokemonCard) => {
    if (!card.tcgplayer?.prices) return null;
    return Object.values(card.tcgplayer.prices)[0]?.market || null;
  };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {cards.map((card, index) => {
          const price = getCardPrice(card);
          const trend = getMarketTrend(card);
          
          return (
            <Link 
              to={`/card/${card.id}`} 
              key={card.id}
              className="focus:outline-none focus:ring-2 focus:ring-pokemon-blue rounded-lg block"
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 card-hover">
                  <CardContent className="p-0 relative">
                    <img
                      src={card.images.small}
                      alt={card.name}
                      className="w-full h-auto object-contain"
                      loading="lazy"
                    />
                    <div className="p-3">
                      <h3 className="font-medium text-sm truncate">{card.name}</h3>
                      <p className="text-xs text-muted-foreground truncate">
                        {card.set.name}
                      </p>
                      
                      {price ? (
                        <div className="mt-2 flex items-center justify-between">
                          <div className="text-sm font-bold text-green-700">
                            ${price.toFixed(2)}
                          </div>
                          {trend && (
                            <Badge 
                              variant="outline"
                              className={`flex items-center gap-1 ${
                                trend === "rising" ? "bg-green-100 text-green-800" : 
                                trend === "falling" ? "bg-red-100 text-red-800" : 
                                "bg-amber-100 text-amber-800"
                              }`}
                            >
                              {trend === "rising" ? <TrendingUp size={14} /> : 
                               trend === "falling" ? <TrendingDown size={14} /> : 
                               <Minus size={14} />}
                               {trend === "rising" ? "Up" : 
                                trend === "falling" ? "Down" : "Stable"}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2 text-xs text-muted-foreground">
                          No price data
                        </div>
                      )}
                      
                      {card.rarity && (
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-black/70 text-white text-xs">
                            {card.rarity}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </Link>
          );
        })}
      </div>

      {pageCount > 1 && (
        <div className="flex justify-center items-center space-x-4 mt-6">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} / {pageCount}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === pageCount}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
